import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import {
  ChevronDown,
  ChevronRight,
  ClockRewind,
  DotsHorizontal,
  Eye,
  InfoCircle,
  Trash02,
} from '@timelyapp/icons/line-icons';
import {
  DropdownMenu,
  type DropdownMenuItem,
  Pill,
  RadixTooltip,
  useCallbackProxy,
} from '@timelyapp/react';
import type { AgentSession } from 'api/agentSessions/agentSessions.api';
import classNames from 'classnames';
import PrivacyIndicator from 'components/PrivacyIndicator';
import { useEdgeToEdgeScroll } from 'hooks/useEdgeToEdgeScroll';
import React, { useCallback, useId, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'utils/viewport';
import { getUserId } from '~redux/selectors/user';
import { ConductorAvatar, ProviderIcon } from './AgentIdentity';
import AgentProjectPicker from './AgentProjectPicker';
import AgentSessionDetails from './AgentSessionDetails';
import AgentSessionHistory from './AgentSessionHistory';
import AgentSessionProject from './AgentSessionProject';
import {
  AgentLinkedWork,
  type AgentTableWorkspaceValue,
  AgentTags,
  ColumnResizer,
  SelectionCheckbox,
  useAgentTableWorkspace,
} from './AgentTableWorkspace';
import styles from './AgentTrackingSessions.module.scss';
import AgentWorkPeriod, { MetricValue, ProjectLabel } from './AgentWorkPeriod';
import {
  type AgentPeriod,
  type AgentVisibility,
  canEditPeriod,
  canEditSession,
  countSessions,
  DEFAULT_SESSION_SORT,
  formatCost,
  formatDuration,
  formatEstimatedCost,
  formatTokens,
  groupPeriodsBySession,
  hasAgentMetric,
  ownsSession,
  type SessionSort,
  type SessionSortKey,
  sessionLiveBadge,
  sharedProjectId,
  sortTrackedSessions,
  summarizePeriods,
  type TrackedSession,
} from './agentTracking.model';
import { playRowMotion } from './liveMotion';
import { SessionProjectSuggestions } from './SessionProjectSuggestions';
import { useMarkProjectsUsed } from './useMarkProjectsUsed';

const sentenceSegmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });

function sessionSummaryPreview(periods: AgentPeriod[]): string {
  const latest = periods
    .filter(period => period.summary?.trim())
    .reduce<AgentPeriod | undefined>(
      (current, period) =>
        !current || Date.parse(period.startedAt) > Date.parse(current.startedAt) ? period : current,
      undefined
    );
  const summary = latest?.summary?.replace(/\s+/g, ' ').trim();
  return summary
    ? (sentenceSegmenter.segment(summary)[Symbol.iterator]().next().value?.segment.trim() ??
        summary)
    : '';
}

/** Publishing is offered until a session is published; there is no way back short of deleting it. */
function publicationItems(visibility: AgentVisibility, publish: (published: boolean) => void) {
  const items: DropdownMenuItem[] = [];
  if (visibility !== 'published') {
    items.push({
      type: 'item',
      label: 'Publish',
      icon: <Eye width={16} height={16} />,
      onSelect: () => publish(true),
    });
  }
  return items;
}

export type AssignProject = (
  sessionId: number,
  projectId: number | null,
  segmentIds?: number[]
) => Promise<void>;

/** Why a session's empty project is a label and not a picker; said on hover. */
export const ASSIGN_DENIED_REASON = "You can't edit this session";

/**
 * A collapsed session row: title line, preview line and cell padding. Rows are measured once
 * on screen, so this only has to be close enough for the scrollbar.
 */
const VIRTUAL_ROW_ESTIMATE = 57;
/** Rows kept rendered past each edge of the viewport, so a scroll never shows a gap. */
const VIRTUAL_OVERSCAN = 8;
/** Rows shown while the scroll element has no measured size yet (jsdom, a first layout). */
const VIRTUAL_FALLBACK_ROWS = VIRTUAL_OVERSCAN * 2;

/** Every column a row may show, for the visible-columns key the memoized rows compare. */
const COLUMN_IDS = [
  'provider',
  'title',
  'project',
  'linked',
  'conductor',
  'tags',
  'seconds',
  'tokens',
  'cost',
];

interface SessionRowsProps {
  mobile: boolean;
  trackedSession: TrackedSession;
  isExpanded: boolean;
  onToggle: (key: string) => void;
  /** Visible column ids, comma-separated, so the row compares by value. */
  visibleColumns: string;
  /**
   * This table's own prefix for the row motion keys: one session can appear in several
   * groups (days, projects), and each copy must be measured on its own.
   */
  flipPrefix: string;
  canSelect: boolean;
  isSelected: boolean;
  saving: boolean;
  columnCount: number;
  activityColumns: string;
  showDates: boolean;
  projectName?: string;
  scopeAssignmentToPeriods: boolean;
  onAssign?: AssignProject;
  /** The table assigns, but not this session: its empty label stays quiet and says this on hover. */
  deniedReason?: string;
  changeSession?: (request: () => Promise<AgentSession>) => Promise<AgentSession>;
  markProjectsUsed: (projectIds: number[]) => void;
  workspaceAssign?: AgentTableWorkspaceValue['onAssign'];
  onPublish?: AgentTableWorkspaceValue['onPublish'];
  publish?: AgentTableWorkspaceValue['publish'];
  requestDelete?: AgentTableWorkspaceValue['requestDelete'];
  /** The table has an actions column; publish and delete are left out where not the viewer's. */
  showActions: boolean;
  owned: boolean;
}

/**
 * One session's row and its expandable periods. Memoized: a live refresh keeps
 * the period objects that did not change (see markLiveChanges), so only the
 * sessions it touched render again instead of the whole table.
 */
const SessionRows = React.memo(function SessionRows({
  mobile,
  trackedSession,
  isExpanded,
  onToggle,
  visibleColumns,
  flipPrefix,
  canSelect,
  isSelected,
  saving,
  columnCount,
  activityColumns,
  showDates,
  projectName,
  scopeAssignmentToPeriods,
  onAssign,
  deniedReason,
  changeSession,
  markProjectsUsed,
  workspaceAssign,
  onPublish,
  publish,
  requestDelete,
  showActions,
  owned,
}: SessionRowsProps) {
  const visibleIds = new Set(visibleColumns.split(','));
  const visible = (id: string) => visibleIds.has(id);
  const key = trackedSession.sessionKey;
  const summaryPreview = isExpanded ? '' : sessionSummaryPreview(trackedSession.periods);
  const periodCount =
    trackedSession.periods.length > 1
      ? `${trackedSession.periods.length} ${trackedSession.periods[0].provider === 'claude_cowork' ? 'summaries' : 'periods'}`
      : '';
  const panelId = `agent-session-${trackedSession.periods[0].id}`;
  const sharedProject = sharedProjectId(trackedSession.periods);
  // Names a project the viewer hasn't loaded only when every period is on it.
  const sharedPeriod = sharedProject === null ? undefined : trackedSession.periods[0];
  const liveBadge = sessionLiveBadge(trackedSession.periods);
  const describedBy = periodCount || summaryPreview ? `${panelId}-summary` : undefined;
  return (
    <SessionProjectSuggestions
      key={key}
      periods={trackedSession.periods}
      enabled={!!changeSession && !!onAssign}
      changeSession={changeSession}
      onAccepted={markProjectsUsed}
    >
      <tr
        className={classNames(styles.sessionRow, {
          [styles.selectedRow]: isSelected,
        })}
        data-live-updated={trackedSession.periods.some(p => p.liveChange) ? '' : undefined}
        data-flip-key={`${flipPrefix}:${key}`}
      >
        {!mobile && visible('provider') && (
          <td className={styles.providerCell} data-label="Provider">
            {canSelect ? (
              <label
                className={styles.providerSelection}
                title={trackedSession.periods[0].providerName}
              >
                <span className={styles.providerVisual}>
                  <ProviderIcon
                    provider={trackedSession.periods[0].provider}
                    name={trackedSession.periods[0].providerName}
                    selectable
                  />
                </span>
                <SelectionCheckbox
                  periods={trackedSession.periods}
                  label={`Select ${trackedSession.title}`}
                />
              </label>
            ) : (
              <ProviderIcon
                provider={trackedSession.periods[0].provider}
                name={trackedSession.periods[0].providerName}
              />
            )}
          </td>
        )}
        {visible('title') && (
          <td className={styles.sessionCell}>
            {mobile && trackedSession.visibility === 'private' && (
              <PrivacyIndicator element="span" className={styles.mobilePrivateBadge} />
            )}

            <button
              type="button"
              className={styles.agentToggle}
              aria-label={trackedSession.title}
              aria-describedby={describedBy || undefined}
              aria-expanded={isExpanded}
              aria-controls={panelId}
              onClick={() => onToggle(key)}
            >
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
              {mobile && visible('provider') && (
                <span className={styles.mobileProvider}>
                  <ProviderIcon
                    provider={trackedSession.periods[0].provider}
                    name={trackedSession.periods[0].providerName}
                  />
                </span>
              )}
              <span className={styles.sessionCopy}>
                <span className={styles.sessionTitleRow}>
                  <strong className={styles.sessionTitle} title={trackedSession.title}>
                    {trackedSession.title}
                  </strong>
                  {trackedSession.periods.length > 1 && (
                    <span className={styles.activityCount} aria-hidden="true">
                      {trackedSession.periods.length}
                    </span>
                  )}
                  {!mobile && trackedSession.visibility === 'private' && (
                    <PrivacyIndicator element="span" className={styles.privateBadge} />
                  )}
                  {liveBadge && (
                    <Pill
                      size="xsmall"
                      variant={liveBadge === 'new' ? 'success' : 'normal'}
                      className={styles.liveBadge}
                    >
                      {liveBadge === 'new' ? 'New' : 'Updated'}
                    </Pill>
                  )}
                </span>
                {(periodCount || summaryPreview) && (
                  <span className={styles.sessionPreview} id={`${panelId}-summary`}>
                    {periodCount && <span className={styles.periodCountLabel}>{periodCount}</span>}
                    {periodCount && summaryPreview && <span aria-hidden="true">·</span>}
                    {summaryPreview && (
                      <span className={styles.sessionPreviewText}>{summaryPreview}</span>
                    )}
                  </span>
                )}
              </span>
            </button>
          </td>
        )}
        {visible('project') && (
          <td data-label="Project">
            {onAssign ? (
              <AgentSessionProject
                periods={trackedSession.periods}
                title={trackedSession.title}
                onAssignPeriod={(period, id) => onAssign(period.sessionId, id, [period.segmentId])}
                onAssign={(projectId, segmentIds) =>
                  onAssign(
                    trackedSession.periods[0].sessionId,
                    projectId,
                    segmentIds ??
                      (scopeAssignmentToPeriods
                        ? trackedSession.periods.map(period => period.segmentId)
                        : undefined)
                  )
                }
              />
            ) : (
              <ProjectLabel
                projectId={sharedProject}
                fallbackName={projectName ?? sharedPeriod?.projectName ?? undefined}
                fallbackColor={sharedPeriod?.projectColor}
                quietWhenEmpty={!!deniedReason}
                emptyTooltip={deniedReason}
              />
            )}
          </td>
        )}
        {visible('linked') && (
          <td data-label="Linked work">
            <AgentLinkedWork
              periods={trackedSession.periods}
              onAssign={workspaceAssign}
              disabled={saving}
            />
          </td>
        )}
        {visible('conductor') && (
          <td data-label="Conductor" className={styles.conductorCell}>
            <div className={styles.conductorContent}>
              <ConductorAvatar userId={trackedSession.periods[0].userId} showName />
            </div>
          </td>
        )}
        {visible('tags') && (
          <td data-label="Tags">
            <AgentTags
              periods={trackedSession.periods}
              onAssign={workspaceAssign}
              disabled={saving}
            />
          </td>
        )}
        {visible('seconds') && (
          <td data-label="Agent time" className={styles.numeric}>
            <MetricValue
              unavailable={trackedSession.periods.some(
                period => !hasAgentMetric(period, 'seconds')
              )}
              label="Agent time"
              reason="Agent time was not reported. Excluded from totals."
            >
              {formatDuration(trackedSession.totals.seconds)}
            </MetricValue>
          </td>
        )}
        {visible('tokens') && (
          <td data-label="Tokens" className={styles.numeric}>
            <strong>
              <MetricValue
                unavailable={trackedSession.periods.some(
                  period => !hasAgentMetric(period, 'tokens')
                )}
                label="Token usage"
                reason="Token usage was not reported. Excluded from totals."
              >
                {formatTokens(trackedSession.totals.tokens)}
              </MetricValue>
            </strong>
          </td>
        )}
        {visible('cost') && (
          <td data-label="Est. cost" className={styles.numeric}>
            <MetricValue
              unavailable={trackedSession.periods.some(period => !hasAgentMetric(period, 'cost'))}
              label="Estimated cost"
              reason="A cost estimate is not available. Excluded from cost totals."
            >
              {formatCost(trackedSession.totals.cost)}
            </MetricValue>
          </td>
        )}
        {showActions && (
          <td className={styles.actionsCell}>
            <AgentSessionHistory
              sessionId={trackedSession.periods[0].sessionId}
              title={trackedSession.title}
            >
              {openHistory => (
                <AgentSessionDetails
                  sessionId={trackedSession.periods[0].sessionId}
                  onAssign={workspaceAssign}
                  onPublish={onPublish}
                >
                  {openDetails => (
                    <DropdownMenu
                      returnFocusOnClose={false}
                      contentClassName={styles.sessionMenu}
                      sideOffset={4}
                      align="end"
                      closeOnSelect
                      trigger={
                        <button
                          type="button"
                          className={styles.sessionActions}
                          aria-label={`Actions for ${trackedSession.title}`}
                          disabled={saving}
                        >
                          <DotsHorizontal width={16} height={16} />
                        </button>
                      }
                      items={[
                        {
                          type: 'item',
                          label: 'More info',
                          icon: <InfoCircle width={16} height={16} />,
                          onSelect: openDetails,
                        },
                        {
                          type: 'item',
                          label: 'View History',
                          icon: <ClockRewind width={16} height={16} />,
                          onSelect: openHistory,
                        },
                        ...(publish
                          ? publicationItems(trackedSession.visibility, published => {
                              void publish?.(trackedSession.periods[0].sessionId, published);
                            })
                          : []),
                        ...(requestDelete
                          ? ([
                              { type: 'separator' },
                              {
                                type: 'item',
                                label: 'Delete session',
                                icon: <Trash02 width={16} height={16} />,
                                variant: 'danger',
                                onSelect: () => requestDelete(trackedSession.periods),
                              },
                            ] satisfies DropdownMenuItem[])
                          : []),
                      ]}
                    />
                  )}
                </AgentSessionDetails>
              )}
            </AgentSessionHistory>
          </td>
        )}
      </tr>
      <tr
        hidden={!isExpanded}
        id={panelId}
        className={styles.detailRow}
        data-flip-key={`${flipPrefix}:${key}:detail`}
      >
        {isExpanded && (
          <td colSpan={columnCount}>
            <ol
              className={styles.activityList}
              aria-label={`${trackedSession.title} activity periods`}
              style={
                {
                  '--agent-activity-columns': activityColumns,
                  '--agent-activity-content-end':
                    ['seconds', 'tokens', 'cost'].find(visible) ?? 'end',
                } as React.CSSProperties
              }
            >
              {trackedSession.periods.map((period, index) => (
                <AgentWorkPeriod
                  onAssign={owned && onAssign ? workspaceAssign : undefined}
                  key={period.id}
                  period={period}
                  showDates={showDates}
                  inheritedProject={
                    !['inherited', 'automatic'].includes(period.projectAssignmentSource ?? '') &&
                    (!period.projectPredictionState ||
                      period.projectPredictionState === 'manual') &&
                    trackedSession.projectIds.length <= 1 &&
                    period.projectId === sharedProjectId(trackedSession.periods)
                  }
                  marker={
                    canSelect ? (
                      <SelectionCheckbox
                        periods={[period]}
                        label={`Select activity ${index + 1} for ${trackedSession.title}`}
                      />
                    ) : undefined
                  }
                  project={
                    onAssign ? (
                      <AgentProjectPicker
                        value={period.projectId}
                        placeholder="Assign project"
                        ariaLabel={`Project for period ${index + 1}`}
                        periods={[period]}
                        onAssignPeriod={(target, id) =>
                          onAssign(target.sessionId, id, [target.segmentId])
                        }
                        onChange={projectId =>
                          onAssign(period.sessionId, projectId, [period.segmentId])
                        }
                      />
                    ) : (
                      <ProjectLabel
                        projectId={period.projectId}
                        fallbackName={projectName ?? period.projectName ?? undefined}
                        fallbackColor={period.projectColor}
                        quietWhenEmpty={!!deniedReason}
                        emptyTooltip={deniedReason}
                      />
                    )
                  }
                  links={
                    isExpanded ? (
                      <>
                        <AgentTags
                          periods={[period]}
                          onAssign={workspaceAssign}
                          disabled={saving}
                        />
                        <AgentLinkedWork
                          periods={[period]}
                          onAssign={workspaceAssign}
                          disabled={saving}
                        />
                      </>
                    ) : undefined
                  }
                  showSeconds={visible('seconds')}
                  showTokens={visible('tokens')}
                  showCost={visible('cost')}
                />
              ))}
            </ol>
          </td>
        )}
      </tr>
    </SessionProjectSuggestions>
  );
}, sameSessionRows);

/** Props compare by identity, except the session, which compares by its periods. */
function sameSessionRows(previous: SessionRowsProps, next: SessionRowsProps) {
  const keys = Object.keys(next) as (keyof SessionRowsProps)[];
  return (
    keys.length === Object.keys(previous).length &&
    keys.every(key =>
      key === 'trackedSession'
        ? previous.trackedSession.periods.length === next.trackedSession.periods.length &&
          previous.trackedSession.periods.every(
            (period, index) => period === next.trackedSession.periods[index]
          )
        : previous[key] === next[key]
    )
  );
}

export default function AgentTrackingSessions({
  periods,
  onAssign,
  canAssign,
  changeSession,
  projectName,
  showDates = false,
  totalLabel = 'Daily total',
  showTotals = true,
  scopeAssignmentToPeriods = false,
  sort: controlledSort,
  onSortChange,
  edgeToEdge = false,
  edgeFades = true,
  virtualizeIn = null,
}: {
  periods: AgentPeriod[];
  /** Review flow: set or clear the project on a whole session or on listed periods. */
  onAssign?: AssignProject;
  /**
   * With onAssign: the sessions the viewer may assign. The others keep a read-only project
   * label that says why on hover. Every session when not given.
   */
  canAssign?: (session: TrackedSession) => boolean;
  /** Sends a suggestion acceptance or undo in turn with the page's other session changes. */
  changeSession?: (request: () => Promise<AgentSession>) => Promise<AgentSession>;
  /** Read-only project label when assignment controls are not supplied. */
  projectName?: string;
  showDates?: boolean;
  totalLabel?: string;
  showTotals?: boolean;
  /** A grouped row may contain only part of a session. */
  scopeAssignmentToPeriods?: boolean;
  sort?: SessionSort;
  onSortChange?: (sort: SessionSort) => void;
  /** Let the table scroll out to the edges of its clipping container instead of stopping at the column. */
  edgeToEdge?: boolean;
  /**
   * Draw the fades that mark content hidden under a gutter on this frame. Off for a frame as tall
   * as a whole virtual table (the live board), which draws its own, bounded to its scroll viewport.
   */
  edgeFades?: boolean;
  /**
   * The element the table scrolls in. Given, only the sessions in view (and a few past each
   * edge) are rendered, so a workspace-wide board with thousands of sessions stays light
   * (TT-6334). Each session then gets its own tbody, measured as it expands.
   */
  virtualizeIn?: HTMLElement | null;
}) {
  const workspace = useAgentTableWorkspace();
  const markProjectsUsed = useMarkProjectsUsed();
  const mobile = useMediaQuery('(max-width: 48rem)');
  const viewerId = useSelector(getUserId);
  const canSelect = workspace?.canSelect ?? false;
  const visible = (id: string) =>
    workspace?.table.getColumn(id)?.getIsVisible() ?? !['select', 'tags', 'linked'].includes(id);
  const columns = workspace?.table.getVisibleLeafColumns();
  // More info is there wherever the table edits; publish and delete only on the viewer's own rows.
  const hasActions = !!workspace?.requestDelete || !!workspace?.onAssign;
  const columnCount = (columns?.length ?? 7) + Number(hasActions);
  // Group checkboxes select only what the viewer may change (TT-6346), and like the row
  // checkboxes leave out every period of a session they may change only part of.
  const selectablePeriods = useMemo(() => {
    const readOnly = new Set(
      periods.filter(period => !canEditPeriod(period, viewerId)).map(period => period.sessionKey)
    );
    return periods.filter(period => !readOnly.has(period.sessionKey));
  }, [periods, viewerId]);
  const activityColumns = columns
    ? `${columns.map(column => `[${column.id}] ${column.getSize()}px`).join(' ')} [end]${hasActions ? ' 40px' : ''}`
    : '[provider] 40px [title] 1fr [project] 19% [conductor] 48px [seconds] 10% [tokens] 10% [cost] 10% [end]';
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  useEdgeToEdgeScroll(frame, scroller, edgeToEdge);

  const [localSort, setLocalSort] = useState<SessionSort>(DEFAULT_SESSION_SORT);
  const sort = controlledSort ?? localSort;
  const sessions = useMemo(
    () => sortTrackedSessions(groupPeriodsBySession(periods), sort),
    [periods, sort]
  );
  // A fresh virtualizer has a 0 × 0 viewport until its first layout effect, which would make
  // its first render fall back to every row; its initial size is read once per scroll element.
  const initialRect = useMemo(
    () =>
      virtualizeIn
        ? { width: virtualizeIn.clientWidth, height: virtualizeIn.clientHeight }
        : undefined,
    [virtualizeIn]
  );
  // Stable per sessions: a new function each render makes the virtualizer measure everything again.
  const getItemKey = useCallback(
    (index: number) => sessions[index]?.sessionKey ?? index,
    [sessions]
  );
  // Always called (hooks cannot be conditional); without a scroll element it tracks nothing.
  const virtualizer = useVirtualizer({
    count: virtualizeIn ? sessions.length : 0,
    getScrollElement: () => virtualizeIn,
    estimateSize: () => VIRTUAL_ROW_ESTIMATE,
    overscan: VIRTUAL_OVERSCAN,
    getItemKey,
    initialRect,
  });
  function changeSort(key: SessionSortKey) {
    const next: SessionSort = {
      key,
      direction:
        sort.key === key
          ? sort.direction === 'desc'
            ? 'asc'
            : 'desc'
          : key === 'title'
            ? 'asc'
            : 'desc',
    };
    (onSortChange ?? setLocalSort)(next);
  }
  function sortHeader(key: SessionSortKey, label: string) {
    if (!visible(key)) return null;
    const selected = sort.key === key;
    return (
      <th
        scope="col"
        className={key === 'title' ? undefined : styles.numeric}
        aria-sort={selected ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button type="button" className={styles.sortButton} onClick={() => changeSort(key)}>
          {label}
          <span aria-hidden="true">{selected ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
        </button>
        <ColumnResizer id={key} />
      </th>
    );
  }
  // Per periods, not per render: a live board renders on every change and these walk every period.
  const total = useMemo(() => summarizePeriods(periods), [periods]);
  const sessionCount = useMemo(() => countSessions(periods), [periods]);
  const estimatedCost = useMemo(
    () => formatEstimatedCost(periods, total.cost),
    [periods, total.cost]
  );
  const flipId = useId();
  // After every commit: rows a live refresh moved or added glide and pop into place. Without a
  // fresh capture from that refresh it returns at once, so other renders never animate.
  useLayoutEffect(() => {
    playRowMotion();
  });

  const toggle = useCallback((key: string) => {
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  // Rows are memoized (SessionRows), so what they get must keep its identity across renders:
  // the workspace object is rebuilt on every render, its callbacks are routed through proxies.
  const visibleColumns = COLUMN_IDS.filter(visible).join(',');
  const stableAssign = useCallbackProxy<AssignProject>((...args) => onAssign!(...args));
  const stableChangeSession = useCallbackProxy((request: () => Promise<AgentSession>) =>
    changeSession!(request)
  );
  const stableMarkProjectsUsed = useCallbackProxy(markProjectsUsed);
  const stableWorkspaceAssign = useCallbackProxy<NonNullable<SessionRowsProps['workspaceAssign']>>(
    (...args) => workspace!.onAssign!(...args)
  );
  const stableOnPublish = useCallbackProxy<NonNullable<SessionRowsProps['onPublish']>>((...args) =>
    workspace!.onPublish!(...args)
  );
  const stablePublish = useCallbackProxy<NonNullable<SessionRowsProps['publish']>>((...args) =>
    workspace!.publish!(...args)
  );
  const stableRequestDelete = useCallbackProxy<NonNullable<SessionRowsProps['requestDelete']>>(
    (...args) => workspace!.requestDelete!(...args)
  );
  // Rails says which sessions the viewer may change (can_edit); a caller can narrow it further.
  const assignable = (trackedSession: TrackedSession) =>
    !!onAssign && canEditSession(trackedSession, viewerId) && (canAssign?.(trackedSession) ?? true);
  const renderSession = (trackedSession: TrackedSession) => {
    const editable = canEditSession(trackedSession, viewerId);
    const owned = ownsSession(trackedSession, viewerId);
    return (
      <SessionRows
        mobile={mobile}
        key={trackedSession.sessionKey}
        trackedSession={trackedSession}
        isExpanded={expanded.has(trackedSession.sessionKey)}
        onToggle={toggle}
        visibleColumns={visibleColumns}
        flipPrefix={flipId}
        canSelect={canSelect && editable}
        isSelected={
          !!workspace && trackedSession.periods.some(p => workspace.selected.has(p.segmentId))
        }
        saving={!!workspace?.saving}
        columnCount={columnCount}
        activityColumns={activityColumns}
        showDates={showDates}
        projectName={projectName}
        // Someone else's session is edited only in the periods Rails showed (TT-6346).
        scopeAssignmentToPeriods={scopeAssignmentToPeriods || !owned}
        onAssign={assignable(trackedSession) ? stableAssign : undefined}
        deniedReason={onAssign && !assignable(trackedSession) ? ASSIGN_DENIED_REASON : undefined}
        changeSession={changeSession && stableChangeSession}
        markProjectsUsed={stableMarkProjectsUsed}
        workspaceAssign={workspace?.onAssign && editable ? stableWorkspaceAssign : undefined}
        onPublish={workspace?.onPublish && owned ? stableOnPublish : undefined}
        publish={workspace?.publish && owned ? stablePublish : undefined}
        requestDelete={workspace?.requestDelete && owned ? stableRequestDelete : undefined}
        owned={owned}
        showActions={hasActions}
      />
    );
  };

  return (
    <div
      ref={setFrame}
      className={classNames(
        styles.root,
        edgeToEdge && styles.edgeToEdge,
        edgeToEdge && !edgeFades && styles.ownFades
      )}
    >
      <div ref={setScroller} className={styles.tableScroll}>
        <table
          className={styles.table}
          aria-label="Agent sessions"
          style={
            workspace
              ? ({
                  '--agent-table-width': `${workspace.table.getTotalSize() + (hasActions ? 40 : 0)}px`,
                  width: mobile
                    ? '100%'
                    : `${workspace.table.getTotalSize() + (hasActions ? 40 : 0)}px`,
                } as React.CSSProperties)
              : undefined
          }
        >
          <colgroup>
            {columns ? (
              columns.map(column => (
                <col
                  key={column.id}
                  // Session and expanded periods share the same resolved column widths.
                  style={{ width: column.getSize() }}
                />
              ))
            ) : (
              <>
                <col className={styles.providerColumn} />
                <col className={styles.agentColumn} />
                <col className={styles.projectColumn} />
                <col className={styles.conductorColumn} />
                <col className={styles.timeColumn} />
                <col className={styles.tokensColumn} />
                <col className={styles.costColumn} />
              </>
            )}
            {hasActions && <col className={styles.actionsColumn} />}
          </colgroup>
          <thead>
            <tr>
              {visible('provider') && (
                <th scope="col" className={styles.providerCell} aria-label="Provider">
                  {canSelect && (
                    <label className={styles.providerHeading}>
                      <SelectionCheckbox
                        periods={selectablePeriods}
                        label="Select all sessions in group"
                      />
                    </label>
                  )}
                  <ColumnResizer id="provider" />
                </th>
              )}
              {sortHeader('title', 'Session')}
              {visible('project') && (
                <th scope="col">
                  Project
                  <ColumnResizer id="project" />
                </th>
              )}
              {visible('linked') && (
                <th scope="col">
                  Linked work
                  <ColumnResizer id="linked" />
                </th>
              )}
              {visible('conductor') && (
                <th scope="col" className={styles.conductorCell}>
                  <RadixTooltip tooltip="Conductor" side="top">
                    <span className={styles.conductorHeading} tabIndex={0} aria-label="Conductor">
                      Conductor
                    </span>
                  </RadixTooltip>
                  <ColumnResizer id="conductor" />
                </th>
              )}
              {visible('tags') && (
                <th scope="col">
                  Tags
                  <ColumnResizer id="tags" />
                </th>
              )}
              {sortHeader('seconds', 'Agent time')}
              {sortHeader('tokens', 'Tokens')}
              {sortHeader('cost', 'Est. cost')}
              {hasActions && <th scope="col" aria-label="Actions" className={styles.actionsCell} />}
            </tr>
          </thead>
          {virtualizeIn ? (
            <VirtualSessionBodies
              virtualizer={virtualizer}
              sessions={sessions}
              columnCount={columnCount}
              render={renderSession}
            />
          ) : (
            <tbody>{sessions.map(renderSession)}</tbody>
          )}
          {showTotals && (
            <tfoot aria-label={totalLabel}>
              <tr className={styles.totalsRow} data-flip-key={`${flipId}:total`}>
                {visible('provider') && <td />}
                <td className={styles.totalCaptionCell}>
                  <strong>{totalLabel}</strong>
                  <div>
                    {formatCount(sessionCount, 'session')} ·{' '}
                    {formatCount(periods.length, 'activity period')}
                  </div>
                </td>
                {visible('project') && <td />}
                {visible('linked') && <td />}
                {visible('conductor') && <td />}
                {visible('tags') && <td />}
                {visible('seconds') && (
                  <td data-label="Agent time" className={styles.numeric}>
                    <strong>{formatDuration(total.seconds)}</strong>
                  </td>
                )}
                {visible('tokens') && (
                  <td data-label="Tokens" className={styles.numeric}>
                    <strong>{formatTokens(total.tokens)}</strong>
                  </td>
                )}
                {visible('cost') && (
                  <td data-label="Est. cost" className={styles.numeric}>
                    <strong>{estimatedCost}</strong>
                  </td>
                )}
                {hasActions && <td className={styles.actionsCell} />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function formatCount(count: number, label: string) {
  return `${count} ${label}${count === 1 ? '' : 's'}`;
}

/**
 * The rows in view, one tbody per session so an expanded session is measured as one
 * item, between two spacer rows that stand in for everything above and below. Table
 * rows cannot be positioned, so the spacers keep the scroll height honest instead.
 */
function VirtualSessionBodies({
  virtualizer,
  sessions,
  columnCount,
  render,
}: {
  virtualizer: Virtualizer<HTMLElement, Element>;
  sessions: TrackedSession[];
  columnCount: number;
  render: (session: TrackedSession) => React.ReactNode;
}) {
  const items = virtualizer.getVirtualItems();
  // A scroll element without a measured height (not laid out yet, or jsdom) yields no items at
  // all; show the first rows rather than none, or every row, until it has one.
  if (!items.length && sessions.length)
    return <tbody>{sessions.slice(0, VIRTUAL_FALLBACK_ROWS).map(render)}</tbody>;
  const first = items[0];
  const last = items[items.length - 1];
  const before = first ? first.start : 0;
  const after = last ? virtualizer.getTotalSize() - last.end : 0;
  const spacer = (height: number) => (
    <tbody aria-hidden="true">
      <tr>
        <td colSpan={columnCount} className={styles.virtualSpacer} style={{ height }} />
      </tr>
    </tbody>
  );
  return (
    <>
      {before > 0 && spacer(before)}
      {items.map(item => {
        const trackedSession = sessions[item.index];
        if (!trackedSession) return null;
        return (
          <tbody key={item.key} data-index={item.index} ref={virtualizer.measureElement}>
            {render(trackedSession)}
          </tbody>
        );
      })}
      {after > 0 && spacer(after)}
    </>
  );
}
